import { Avatar, Box, Button, Divider, IconButton, Tab, Tabs, Typography } from "@mui/material"
import {
    LogoutOutlined as LogoutOutlinedIcon,
} from '@mui/icons-material'
import { getAuthService } from "../services/authService"
import { useAppSelector } from "../redux/hooks"
import { GenericSpinner } from "../components/GenericSpinner/GenericSpinner"
import { useState } from "react"
import { MyAccountInformation } from "../components/MyAccount/MyAccountInformation"
import { MyAccountClubhouseReservationRequests } from "../components/MyAccount/MyAccountClubhouseReservationRequests"
import { MyAccountEmailPreferences } from "../components/MyAccount/MyAccountEmailPreferences"
import { MyAccountPrivacy } from "../components/MyAccount/MyAccountPrivacy"
import { MyAccountMembership } from "../components/MyAccount/MyAccountMembership/MyAccountMembership"

enum MyAccountTabs {
    ACCOUNT_INFORMATION = 'account_information',
    MEMBERHSIP = 'membership',
    RESERVATION_REQUESTS = 'reservation_requests',
    EMAIL_PREFERENCES = 'email_preferences',
    PRIVACY = 'privacy'
}

export const MyAccount = () => {

    const user = useAppSelector(state => state.currentUser.account)

    const [activeTab, setActiveTab] = useState<MyAccountTabs>(MyAccountTabs.ACCOUNT_INFORMATION)
    
    const handleSignOut = () => {
        const authService = getAuthService()
        authService.logout()
    }

    if (!user) {
        return <GenericSpinner />
    }

    return (
        <Box>
            <Box className="my-account__header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 3, px: 3, pb: 1 }}>
                <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 200 }}>My Account</Typography>
                <IconButton onClick={handleSignOut}>
                    <LogoutOutlinedIcon />
                </IconButton>
            </Box>
            <Divider />
            <Box className="my-account__content" sx={{ display: { xs: 'none', sm: 'flex', width: '100%' }, mt: 3, gap: '32px' }}>
                <Box className="my-account__sidebar" sx={{ px: 3, minWidth: '275px' }}>
                    {/* <Avatar src={} /> */}
                    <Avatar sx={{ bgcolor: 'primary.main', mx: 2 }}>{ user?.username.charAt(0).toUpperCase() }</Avatar>
                    <Box className="my-account__sidebar-links" sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Button
                            variant='text'
                            onClick={() => setActiveTab(MyAccountTabs.ACCOUNT_INFORMATION)}
                            sx={{
                                textTransform: 'none',
                                mt: 2,
                                color: activeTab === MyAccountTabs.ACCOUNT_INFORMATION ? '' : '#000',
                                p: 0.5,
                                display: 'flex',
                                justifyContent: 'flex-start'
                            }}
                        >
                            Account Information
                        </Button>
                        <Button
                            variant='text'
                            onClick={() => setActiveTab(MyAccountTabs.MEMBERHSIP)}
                            sx={{
                                textTransform: 'none',
                                mt: 1,
                                color: activeTab === MyAccountTabs.MEMBERHSIP ? '' : '#000',
                                p: 0.5,
                                display: 'flex',
                                justifyContent: 'flex-start'
                            }}
                        >
                            Membership
                        </Button>
                        <Button
                            variant='text'
                            onClick={() => setActiveTab(MyAccountTabs.RESERVATION_REQUESTS)}
                            sx={{
                                textTransform: 'none',
                                mt: 1,
                                color: activeTab === 'reservation_requests' ? '' : '#000',
                                p: 0.5,
                                display: 'flex',
                                justifyContent: 'flex-start'
                            }}
                        >
                            Clubhouse Reservation Requests
                        </Button>
                        <Button
                            variant='text'
                            onClick={() => setActiveTab(MyAccountTabs.EMAIL_PREFERENCES)}
                            sx={{
                                textTransform: 'none',
                                mt: 1,
                                color: activeTab === MyAccountTabs.EMAIL_PREFERENCES ? '' : '#000',
                                p: 0.5,
                                display: 'flex',
                                justifyContent: 'flex-start'
                            }}
                        >
                            Email Preferences
                        </Button>
                        <Button
                            variant='text'
                            onClick={() => setActiveTab(MyAccountTabs.PRIVACY)}
                            sx={{
                                textTransform: 'none',
                                mt: 1,
                                color: activeTab === MyAccountTabs.PRIVACY ? '' : '#000',
                                p: 0.5,
                                display: 'flex',
                                justifyContent: 'flex-start'
                            }}
                        >
                            Privacy
                        </Button>
                    </Box>
                </Box>
                { activeTab === MyAccountTabs.ACCOUNT_INFORMATION && <MyAccountInformation /> }
                { activeTab === MyAccountTabs.MEMBERHSIP && <MyAccountMembership /> }
                { activeTab === MyAccountTabs.RESERVATION_REQUESTS && <MyAccountClubhouseReservationRequests /> }
                { activeTab === MyAccountTabs.EMAIL_PREFERENCES && <MyAccountEmailPreferences /> }
                { activeTab === MyAccountTabs.PRIVACY && <MyAccountPrivacy /> }
            </Box>
            <Box className="my-account__content" sx={{ display: { xs: 'flex', flexDirection: 'column', sm: 'none' }, mt: 3, pb: 3, gap: '32px' }}>
                <Tabs value={activeTab} variant='scrollable' onChange={(e, v) => setActiveTab(v)} sx={{ width: '100%' }}>
                    <Tab label="Account Information" value={MyAccountTabs.ACCOUNT_INFORMATION} />
                    <Tab label="Membership" value={MyAccountTabs.MEMBERHSIP} />
                    <Tab label="Clubhouse Reservation Requests" value={MyAccountTabs.RESERVATION_REQUESTS} />
                    <Tab label="Email Preferences" value={MyAccountTabs.EMAIL_PREFERENCES} />
                    <Tab label="Privacy" value={MyAccountTabs.PRIVACY} />
                </Tabs>
                <TabPanel value={activeTab} index="account_information">
                    <MyAccountInformation />
                </TabPanel>
                <TabPanel value={activeTab} index="membership">
                    <MyAccountMembership />
                </TabPanel>
                <TabPanel value={activeTab} index="email_preferences">
                    <MyAccountEmailPreferences />
                </TabPanel>
                <TabPanel value={activeTab} index="reservation_requests">
                    <MyAccountClubhouseReservationRequests />
                </TabPanel>
                <TabPanel value={activeTab} index="privacy">
                    <MyAccountPrivacy />
                </TabPanel>
            </Box>
        </Box>
    )
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: string;
    value: string;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}